import { Link } from "react-router-dom";
import { ArrowRight, Quote, MapPin } from "lucide-react";
import { FOUNDER } from "../data/content";
import Reveal from "../components/Reveal";
import Seo from "../components/Seo";

const FOUNDER_LD = {
  "@context": "https://schema.org",
  "@type": "Person",
  name: "Shannen Alexander",
  alternateName: "Shannen \"Alex\" Alexander",
  jobTitle: "Founder & CEO",
  worksFor: { "@type": "Organization", name: "The Mogul Group Advisors" },
  address: { "@type": "PostalAddress", addressLocality: "Dallas", addressRegion: "TX", addressCountry: "US" },
  description: "Entrepreneur, business strategist, and operations leader with 15+ years of leadership experience across hospitality and enterprise sales.",
  image: FOUNDER.photo,
};

export default function Founder() {
  return (
    <div data-testid="page-founder">
      <Seo
        title={`Meet the Founder — ${FOUNDER.name}`}
        description="Meet Shannen 'Alex' Alexander, Founder & CEO of The Mogul Group Advisors. 15+ years of leadership across hospitality, enterprise sales, and business strategy in Dallas–Fort Worth."
        path="/founder"
        image={FOUNDER.photo}
        jsonLd={FOUNDER_LD}
      />

      {/* HERO */}
      <section className="bg-white pt-20 pb-16">
        <div className="container-wide grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="relative">
              <img src={FOUNDER.photo} alt={`${FOUNDER.name}, ${FOUNDER.title}`} className="w-full h-[620px] object-cover object-top rounded-sm shadow-[0_12px_40px_rgba(0,0,0,0.12)]" data-testid="founder-photo" />
              <div className="absolute -bottom-4 -right-4 hidden md:block bg-[#0A0A0A] text-white px-5 py-3 border border-white/10">
                <div className="overline" style={{ color: "#C19B54" }}>Dallas · TX</div>
                <div className="text-sm mt-1">{FOUNDER.title}</div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="overline mb-4">Meet the Founder</div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]" data-testid="founder-name">
              {FOUNDER.name}.
            </h1>
            <div className="mt-6 flex items-center gap-3 text-sm text-[#6B7280]">
              <span className="text-gold">◆</span> {FOUNDER.title}
            </div>
            <div className="mt-8 border-l-2 border-gold pl-6">
              <p className="font-serif text-xl md:text-2xl leading-snug text-[#0A0A0A]" data-testid="founder-quote">
                "I've spent my career helping businesses move from operational chaos to structured, profitable systems. My goal is simple: <span className="italic text-gold">{FOUNDER.quote}</span>"
              </p>
              <p className="mt-4 text-sm text-[#6B7280]">— {FOUNDER.short}, {FOUNDER.title}</p>
            </div>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link to="/contact" className="btn btn-primary" data-testid="founder-cta-book">Book a Consultation <ArrowRight className="w-4 h-4" strokeWidth={1.5} /></Link>
              <a href="mailto:info@TheMogulAdvisors.com" className="btn btn-secondary" data-testid="founder-cta-contact">Contact Alex</a>
            </div>
          </div>
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="bg-[#0A0A0A] text-white grain">
        <div className="container-wide relative z-10 py-14 grid grid-cols-2 md:grid-cols-4 gap-8" data-testid="founder-stats">
          {FOUNDER.stats.map((s, i) => (
            <Reveal key={i} delay={i * 60}>
              <div className="text-center md:text-left" data-testid={`founder-stat-${i}`}>
                <div className="font-serif text-5xl leading-none">
                  <span className="text-gold">{s.value}</span><span className="text-white text-3xl">{s.suffix}</span>
                </div>
                <div className="mt-3 text-xs uppercase tracking-widest text-[#A1A1AA] leading-relaxed">{s.label}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* BIO */}
      <section className="bg-white">
        <div className="container-wide py-20 md:py-28 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline mb-4">Founder Story</div>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-tight">{FOUNDER.tagline}</h2>
            <p className="mt-6 text-[#4B5563] leading-relaxed">{FOUNDER.subtagline}</p>
          </div>
          <div className="lg:col-span-7 space-y-6 text-[#4B5563] leading-relaxed">
            {FOUNDER.bio.map((p, i) => (
              <p key={i} className="text-[15px]" data-testid={`founder-bio-${i}`}>{p}</p>
            ))}
          </div>
        </div>
      </section>

      {/* THREE CHAPTERS */}
      <section className="bg-[#F7F7F8]">
        <div className="container-wide py-20 md:py-28">
          <div className="max-w-3xl">
            <div className="overline mb-4">The Journey</div>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-tight">Three Chapters, One Through-Line.</h2>
            <p className="mt-4 text-[#4B5563] leading-relaxed">
              Operations, hospitality, retail, sales leadership, and advisory — each chapter compounds into the firm Alex leads today.
            </p>
          </div>
          <div className="mt-14 grid md:grid-cols-3 gap-6" data-testid="founder-chapters">
            {FOUNDER.chapters.map((c, i) => (
              <Reveal key={c.n} delay={i * 80}>
                <div className="bg-white border-t-2 border-gold p-8 h-full flex flex-col" data-testid={`chapter-${c.n}`}>
                  <div className="font-serif text-3xl text-gold leading-none">{c.n}</div>
                  <div className="mt-4 overline">{c.title}</div>
                  <h3 className="mt-2 font-serif text-xl leading-snug">{c.heading}</h3>
                  <p className="mt-4 text-sm text-[#4B5563] leading-relaxed flex-1">{c.body}</p>
                  <div className="mt-6 pt-4 border-t border-gray-100 flex flex-wrap gap-2">
                    {c.tags.map((t) => (
                      <span key={t} className="text-[11px] uppercase tracking-widest text-[#6B7280] border border-gray-200 px-2 py-1">{t}</span>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className="bg-white">
        <div className="container-wide py-20 md:py-28 max-w-4xl text-center">
          <Quote className="w-8 h-8 text-gold mx-auto" strokeWidth={1.5} />
          <div className="overline mt-6 mb-4">Alex's Mission</div>
          <h2 className="font-serif text-3xl md:text-5xl tracking-tight leading-tight" data-testid="founder-mission-headline">{FOUNDER.missionHeadline}</h2>
          <p className="mt-8 text-lg text-[#4B5563] leading-relaxed max-w-2xl mx-auto">{FOUNDER.missionBody}</p>
          <p className="mt-6 text-sm text-[#6B7280]">— {FOUNDER.name}</p>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-[#0A0A0A] text-white">
        <div className="container-wide py-20 text-center max-w-3xl mx-auto">
          <h2 className="font-serif text-3xl md:text-4xl leading-tight">Ready to build a business that runs with clarity?</h2>
          <p className="mt-4 text-[#D1D5DB]">Start with a complimentary Growth Assessment or book a direct consultation.</p>
          <div className="mt-10 flex gap-4 justify-center flex-wrap">
            <Link to="/growth-assessment" className="btn btn-primary" data-testid="founder-final-cta-ga">Get Your Free Growth Assessment</Link>
            <Link to="/contact" className="btn btn-dark" data-testid="founder-final-cta-contact">Schedule a Consultation</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
